// Shared opportunity-ranking pipeline. Mirrors the logic used by
// PrioritizationOpportunities.vue so other surfaces (Today hero, Intelligence
// deep-dive) can reuse the same sorted list of zone opportunities without
// remounting the Prioritization panel.

import { ACTION_LIBRARY } from '@/prioritization/settings'
import {
  patternFromLadder,
  computeHeadroom
} from '@/prioritization/classifier'
import { computeLadder } from '@/prioritization/ladder'
import { resolveZoneCeiling } from '@/prioritization/ceilingsRef'

/**
 * Build a per-zone overrides map from a detection-zone store snapshot.
 *
 * Returns `{zoneName → { sectorOverride, pastPeakEngagementP95 }}`
 * — a richer shape than the original (string-only) one. The richer
 * shape is what resolveZoneCeiling now consumes: it can apply BOTH
 * the admin's "behave like sector X" override AND the nightly
 * pipeline's 90-day calibrated Emax in a single lookup.
 *
 * Only zones with at least one meaningful piece of data appear in
 * the map; zones whose settings have neither a SectorOverride nor a
 * PastPeakEngagement_p95 stays absent, so consumers pay nothing for it.
 *
 * Safe against either string (raw JSON) or already-parsed
 * DetectionZoneSettings; returns {} if the store snapshot is missing.
 */
export function buildZoneOverridesMap(detectionZoneStore) {
  const map = {}
  const zones = detectionZoneStore?.detectionzones || []
  for (const z of zones) {
    const name = z.DetectionZoneName || z.name
    if (!name) continue
    let settings = z.DetectionZoneSettings
    if (typeof settings === 'string') {
      try { settings = JSON.parse(settings || '{}') }
      catch (_) { settings = {} }
    }
    const sectorOverride = settings?.SectorOverride || null
    // Rung-1 "your best weeks" targets, persisted nightly by the ETL
    // (tools/zone_calibration.py) as the robust top-quartile-median of each
    // zone's trailing weekly series. Rung1_Engagement supersedes the legacy
    // PastPeakEngagement_p95 (which was a plain 95th-percentile, single value,
    // engagement only); Rung1_Reach is the new reach twin. Both feed the
    // four-rung ladder (ladder.js) as the lowest own-relative rung, and the
    // hero's "in your best weeks…" line via storeEfficiency.js.
    const rung1Engagement = asRate(
      settings?.Rung1_Engagement ?? settings?.PastPeakEngagement_p95 ?? settings?.SelfCalibrated_Emax
    )
    const rung1Reach = asRate(settings?.Rung1_Reach)
    // The zone's own week-to-week swing in reach (std. dev. of its weekly reach),
    // calibrated with Rung1_Reach — the ladder's margin for the reach lever, so a
    // gap only counts when it beats the zone's normal wobble. 0 is a valid value.
    const reachWobble = asUnitInterval(settings?.Rung1_ReachWobble)
    // WHICH MONTHS those best weeks fell in. "Its own best weeks" is silently
    // seasonal — a zone that peaks in coat season is being compared with October
    // every July — so the surfaces can name when they were and let the reader judge.
    // A fact, with no claim about cause attached.
    const reachPeakMonths = Array.isArray(settings?.Rung1_ReachPeakMonths)
      ? settings.Rung1_ReachPeakMonths.filter((m) => Number.isInteger(m) && m >= 1 && m <= 12)
      : []

    // Zone CONTEXT (coach/zone_context.py, derive-then-confirm) that governs the
    // REACH lever (docs/12): reach is position-bound, so a 'destination'/back-
    // corner or a 'considered' destination category isn't a reach problem — we
    // gate reach off there (CONFIRMED only) and benchmark reach within same
    // traffic-exposure zones. {value, source} per attribute.
    const ctx = settings?.coachContext || {}
    const [categoryMode, categoryModeConfirmed] = ctxAttr(ctx, 'category_mode')
    const [trafficExposure, trafficExposureConfirmed] = ctxAttr(ctx, 'traffic_exposure')

    if (sectorOverride || rung1Engagement !== null || rung1Reach !== null
        || categoryMode !== null || trafficExposure !== null) {
      map[name] = {
        sectorOverride,
        rung1Engagement,
        rung1Reach,
        reachWobble,
        reachPeakMonths,
        categoryMode,
        categoryModeConfirmed,
        trafficExposure,
        trafficExposureConfirmed,
      }
    }
  }
  return map
}

// Coerce a persisted value to a fraction in [0, 1] (0 allowed), else null.
function asUnitInterval(x) {
  const v = Number(x)
  return x != null && Number.isFinite(v) && v >= 0 && v <= 1 ? v : null
}

// Coerce a persisted rate to a valid fraction in (0, 1], else null.
function asRate(x) {
  return (typeof x === 'number' && Number.isFinite(x) && x > 0 && x <= 1) ? x : null
}

// Read a coachContext attribute → [value, isConfirmed]. Derived guesses count as
// a value (for grouping) but only CONFIRMED values hard-gate the reach lever.
function ctxAttr(ctx, key) {
  const e = ctx && ctx[key]
  if (!e || typeof e !== 'object') return [null, false]
  const value = typeof e.value === 'string' ? e.value : null
  return [value, e.source === 'confirmed']
}

// Stable seed so action-rotation stays consistent per zone/pattern.
function hashStr(s = '') {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}

export function pickActions(kind, zoneType = 'product', zoneName = '', n = 3) {
  const libByPattern = ACTION_LIBRARY[kind]
  const libByZone = ACTION_LIBRARY[zoneType]
  const base =
    (Array.isArray(libByPattern) && libByPattern.length)
      ? libByPattern
      : (Array.isArray(libByZone) && libByZone.length)
        ? libByZone
        : (ACTION_LIBRARY.neutral || [])

  if (!base.length) return []

  const seen = new Set()
  const pool = []
  for (const a of base) {
    const key = (a.text || '').trim().toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      pool.push(a)
    }
  }
  if (!pool.length) return []

  const L = pool.length
  const start = hashStr(`${zoneType}|${kind}|${zoneName}`) % L
  const out = []
  for (let i = 0; i < Math.min(n, L); i++) {
    out.push(pool[(start + i) % L])
  }
  return out
}

export function extractProductZones(d) {
  if (!d || !d.product_zone_funnels) return []
  const funnels = d.product_zone_funnels
  const visitors = d.product_zone_visitors_counts || {}
  const clear = d.product_zone_clear_lingerers_counts || {}
  // platform_capabilities.md §17.7 Phase 1 — per-zone capability profile from the widget. A tripwire
  // (counts-only) zone can't measure engagement; the signals layer reads THIS,
  // it never re-infers measurability from engagementRate === 0.
  const geometryMap = d.product_zones_geometry || {}
  const estDwell = new Set(d.product_zones_with_estimated_dwell || [])
  const dwellConf = d.product_zones_dwell_confidence || {}
  const dwellMethod = d.product_zones_dwell_method || {}
  const transitSrc = d.product_zones_transit_source || {}
  const totalDur = d.total_duration_per_product_zone || {}

  return Object.keys(funnels).map((z) => {
    const f = funnels[z] || {}
    const v = Number(visitors[z] || 0)
    const c = Number(clear[z] || 0)
    const engagementRate = v > 0 ? c / v : 0
    const avgValue = Number(f.average_product_value || 0)
    const convRaw = Number(f.conversion_rate || 0)
    const conversionRate = Math.min(
      1,
      Math.max(0, convRaw <= 1 ? convRaw : convRaw / 100)
    )

    const geometry = geometryMap[z] || 'polygon'
    const engagementMeasurable = geometry !== 'tripwire'
    const countsOnly = geometry === 'tripwire'
    // Tripwire dwell/transit sub-objects (an ESTIMATE, not a measured value). A
    // "Transition-XC-MLE" method carries TRANSIT (travel) time, not in-zone dwell.
    let dwell = null
    let transit = null
    if (estDwell.has(z) && v > 0) {
      const seconds = Number(totalDur[z] || 0) / v
      const conf = dwellConf[z] ?? null
      if (dwellMethod[z] === 'Transition-XC-MLE') {
        transit = { seconds, confidence: conf, from: transitSrc[z] ?? null }
      } else {
        dwell = { seconds, confidence: conf }
      }
    }

    return {
      zone: z,
      visitors: v,
      clear: c,
      engagementRate,
      avgValue,
      conversionRate,
      // MEASURED conversion (buyers ÷ engaged from fed sales) → the conversion lever
      // graduates from a modelled "(est.)" to a hard number. Set by the nightly
      // conversion-graduation phase; absent/false → still modelled.
      conversionMeasured: !!f.conversion_measured,
      capability: {
        geometry,
        engagementMeasurable,
        dwell,
        transit,
        countsOnly,
      },
    }
  })
}

// Format an attainable delta (already in percentage points) for copy:
// whole pp above 1, one decimal below. Mirrors the old pct() label helper.
function ppLabel(pp) {
  return pp >= 1 ? Math.round(pp) : +pp.toFixed(1)
}

const _LEVER_VERB = { eng: 'Increase Engagement', conv: 'Increase Conversion', reach: 'Increase Reach' }
const _LEVER_WORD = { eng: 'engagement', conv: 'conversion', reach: 'reach' }
const _LEVER_CAP = { eng: 'Engagement', conv: 'Conversion', reach: 'Reach' }

// Same-type (product) cross-zone value lists → the Rung-2 "your best zone"
// distribution the ladder reads. Engagement/conversion are transferable zone
// qualities (a 40%-engaging zone proves another could) so they pool ALL product
// zones. Reach is position-bound (docs/12) so it's grouped by traffic-exposure
// instead — see buildReachPeersByExposure.
// A tripwire (counts-only) zone has no engagement or conversion — its 0 is "not
// measured", not a peer's result, and counting it dragged every polygon zone's peer
// rung down. It still counts for REACH (buildReachPeersByExposure).
function buildPeers(zones) {
  const withV = zones.filter((z) => z.visitors > 0 && !(z.capability && z.capability.countsOnly))
  return {
    engagement: withV.map((z) => z.engagementRate),
    conversion: withV.map((z) => z.conversionRate),
  }
}

// Resolved traffic-exposure of a zone (default 'secondary' when unset) — the
// reach peer-group key.
function exposureOf(ov) {
  return (ov && ov.trafficExposure) || 'secondary'
}

// Reach is suppressed as an active lever when a zone is a CONFIRMED destination —
// sought out, back corner. Its low share of store footfall is set by POSITION, so
// "draw more passers-by in" is not a lever anyone can pull (docs/12). Derived
// guesses never hard-gate; only a person's confirmation does.
//
// Purchase type used to gate this too, independently, and that was too wide. A
// considered purchase on a SECONDARY aisle is still reachable: people take furniture
// seriously, but that does not mean more of them walking past it is worthless. It
// silenced cid 8's Meubelzone the moment someone answered "yes, furniture is a
// considered purchase" — an honest answer to a descriptive question, which removed
// the zone's only lever and so every suggestion it had, with nothing said about it.
function reachGatedOff(ov) {
  if (!ov) return false
  return ov.trafficExposure === 'destination' && ov.trafficExposureConfirmed === true
}

// Reach Rung-2 distribution grouped by traffic-exposure: {exposure: [reachRate…]}.
// A zone benchmarks reach only against same-exposure zones (endcaps vs endcaps),
// so a shelf is never told to match a window.
function buildReachPeersByExposure(zones, zoneOverrides) {
  const byExp = {}
  for (const z of zones) {
    if (!(z.visitors > 0)) continue
    const exp = exposureOf(zoneOverrides[z.zone])
    ;(byExp[exp] = byExp[exp] || []).push(z.reachRate)
  }
  return byExp
}

// Returns the ranked per-zone opportunity list. Each zone is scored by the
// four-rung ladder (ladder.js): within-reach € = gap from current to the
// ACTIVE rung (lowest attainable benchmark above current), across engagement,
// reach and conversion. The biggest-€ lever is the headline and drives the
// nightly recommender action.
//
//   ceilings = resolveZoneCeiling(segmentKey, zoneOverrides[zone], 'product')  → Rung 4
//   rung1*   = zoneOverrides[zone].rung1Engagement/_Reach                       → Rung 1
//   peers    = cross-zone same-type distribution                               → Rung 2
//   cohort   = optional {engagement,reach,conversion} p80 for this sector/size → Rung 3
//              (null until the cross-company cohort ETL has ≥ min stores)
export function computePriorities(currentData, compareToData = null, segmentKey = 'default', zoneOverrides = {}, cohort = null, periodDays = 30) {
  const storeTotalVisitors = Number(currentData?.store_total_visitors || 0)
  const prevStoreTotalVisitors = Number(compareToData?.store_total_visitors || 0)

  // Within-reach € is inherently a total over the selected period (visitor
  // counts are period totals). Normalise it to a 30-day month so every surface
  // can honestly say "€/mo" regardless of the period the picker is on. The
  // picker changes the DATA the estimate is based on (recency); the UNIT stays
  // €/mo. periodDays === 30 (Today's fixed window + the default) → factor 1, so
  // the golden ladder cases are untouched. Rates/% are ratios → never scaled.
  const eurScale = periodDays > 0 ? 30 / periodDays : 1

  // Attach reachRate (zone visitors ÷ store footfall) to every extracted zone
  // so it's available both as the reach-lever current value and in the peer
  // distribution. Reach's Rung 4 is 1.0 (100% of footfall), not a research Tmax.
  const withReach = (arr, total) =>
    arr.map((z) => ({ ...z, reachRate: total > 0 ? z.visitors / total : 0 }))

  const current = withReach(extractProductZones(currentData), storeTotalVisitors)
  const prevMap = compareToData
    ? Object.fromEntries(withReach(extractProductZones(compareToData), prevStoreTotalVisitors).map((z) => [z.zone, z]))
    : {}

  const peers = buildPeers(current)
  const reachPeersByExposure = buildReachPeersByExposure(current, zoneOverrides)

  return current
    .filter((z) => z.visitors > 0)
    .map((z) => {
      const ceilings = resolveZoneCeiling(segmentKey, zoneOverrides[z.zone], 'product')
      const ov = zoneOverrides[z.zone] || {}

      // Reach benchmarks against SAME-exposure zones (docs/12); eng/conv pool all.
      const zonePeers = {
        engagement: peers.engagement,
        conversion: peers.conversion,
        reach: reachPeersByExposure[exposureOf(ov)] || [],
      }

      const cap = z.capability || {}
      const engMeasurable = cap.engagementMeasurable !== false
      const countsOnly = cap.countsOnly === true

      const zctx = {
        engagementRate: z.engagementRate,
        conversionRate: z.conversionRate,
        conversionMeasured: z.conversionMeasured,
        reachRate: z.reachRate,
        visitors: z.visitors,
        storeVisitors: storeTotalVisitors,
        avgValue: z.avgValue,
        rung1Engagement: asRate(ov.rung1Engagement),
        rung1Reach: asRate(ov.rung1Reach),
        reachWobble: asUnitInterval(ov.reachWobble),
        reachPeakMonths: ov.reachPeakMonths || [],
        // A reach rate above 100% is not a share of shoppers, so reach is not a
        // lever here. A tripwire counts every CROSSING: one person walking back and
        // forth through a pass-through area counts several times, while store
        // footfall counts them once. cid 8's Snuisterzone reads 113.1%.
        //
        // The count is not wrong — zone counts are entries, walk-throughs included,
        // and that is documented. What is wrong is calling it a share: "113% of
        // shoppers come here now" is an impossible sentence, and printing it would
        // cost the reader their trust in every other number on the page. Withheld,
        // the same as everything else this platform cannot measure.
        reachGated: reachGatedOff(ov) || (z.reachRate || 0) > 1,
        reachPeersActive: ov.trafficExposureConfirmed === true,
        // platform_capabilities.md §17.7 Phase 2 — measurability drives the reach lever's UNIT (€ for a
        // measured funnel, visitors for a counts-only tripwire zone).
        engagementMeasurable: engMeasurable,
        countsOnly,
      }

      const ladder = computeLadder(zctx, zonePeers, ceilings, cohort)
      const pat = patternFromLadder(ladder)
      const { eHead, cHead } = computeHeadroom(z, ceilings)

      const h = ladder.headline
      const potentialImpact = h ? h.eur : 0
      // platform_capabilities.md §17.7 Phase 2 — the headline magnitude may be € (measured funnel) or a
      // VISITORS count (counts-only tripwire reach). potentialImpact stays € (0 for
      // a visitors headline); withinReachMagnitude/Unit carry the honest opportunity.
      const withinReachUnit = h ? h.unit : 'eur'
      const withinReachMagnitude = h ? h.magnitude : 0
      const activeLeg = h ? ladder.legs[h.lever] : null
      const leverKind = h ? h.kind : 'hold'
      const bestLever = h ? _LEVER_VERB[h.kind] : 'Hold — consistency'
      const deltaPp = activeLeg ? activeLeg.deltaPp : 0
      const leverExplainer = h
        ? `+${ppLabel(deltaPp)}pp ${_LEVER_WORD[h.kind]} to reach ${h.targetRung}${h.est ? ' (est.)' : ''}`
        : 'At your top rung — hold and stay consistent'
      const leverShort = h ? `${_LEVER_CAP[h.kind]} +${ppLabel(deltaPp)}pp` : 'At top rung'

      // Period-over-period trend of within-reach €. The benchmark rungs are
      // held FROZEN (same peers/cohort/rung1) across periods per §2.5 — only
      // the zone's own actuals move — so the trend reflects the store, not
      // benchmark drift.
      let trendPct = null
      let trendClass = 'flat'
      const prev = prevMap[z.zone]
      if (prev) {
        const prevCtx = {
          engagementRate: prev.engagementRate,
          conversionRate: prev.conversionRate,
          reachRate: prev.reachRate,
          visitors: prev.visitors,
          storeVisitors: prevStoreTotalVisitors,
          avgValue: prev.avgValue,
          rung1Engagement: zctx.rung1Engagement,
          rung1Reach: zctx.rung1Reach,
          reachWobble: zctx.reachWobble,
          reachGated: zctx.reachGated,
          reachPeersActive: zctx.reachPeersActive,
        }
        const prevLadder = computeLadder(prevCtx, zonePeers, ceilings, cohort)
        const prevImp = prevLadder.headline ? prevLadder.headline.eur : 0
        if (prevImp > 50) {
          const pct = Math.round(((potentialImpact - prevImp) / prevImp) * 100)
          trendPct = pct > 0 ? `+${pct}` : `${pct}`
          trendClass = pct > 2 ? 'up' : pct < -2 ? 'down' : 'flat'
        }
      }

      return {
        zone: z.zone,
        visitors: z.visitors,
        engagementRate: z.engagementRate,
        conversionRate: z.conversionRate,
        conversionMeasured: z.conversionMeasured,
        reachRate: z.reachRate,
        // The zone's own week-to-week reach swing (the reach lever's margin) — shown on
        // a reach suggestion as the reason it fires.
        reachWobble: zctx.reachWobble,
        // ...and WHEN its best weeks were, so a seasonal benchmark can say so.
        reachPeakMonths: zctx.reachPeakMonths || [],
        avgValue: z.avgValue,
        capability: cap,

        prevVisitors: prev ? prev.visitors : null,
        prevEngagementRate: prev ? prev.engagementRate : null,
        prevConversionRate: prev ? prev.conversionRate : null,
        prevAvgValue: prev ? prev.avgValue : null,

        patternKind: pat.kind,
        patternLabel: pat.label,
        patternClass: pat.class,
        actions: pickActions(pat.kind, z.zoneType || 'product', z.zone, 3),

        // € normalised to a 30-day month (see eurScale). The local raw values
        // above still drive the trend math (both periods share the factor).
        potentialImpact: potentialImpact * eurScale,
        // The factor itself, so the deep-dive's per-pp sensitivity recompute
        // (which rebuilds € from raw visitors) can normalise to €/mo too.
        periodScale: eurScale,
        bestLever,
        leverExplainer,
        leverShort,
        leverKind,
        engPart: ladder.legs.engagement.eur * eurScale,
        convPart: ladder.legs.conversion.eur * eurScale,
        reachPart: ladder.legs.reach.eur * eurScale,
        muted: pat.kind === 'strong',
        kind: pat.kind,
        atCeiling: ladder.atCeiling,

        // Within-reach headline: the € range (widened for the est. conversion
        // lever) and the target rung it closes to — drives the recommender.
        withinReachRange: h ? [h.range[0] * eurScale, h.range[1] * eurScale] : [0, 0],
        withinReachEst: h ? h.est : false,
        // platform_capabilities.md §17.7 Phase 2 — unit-aware headline magnitude ("eur" | "visitors").
        withinReachUnit,
        withinReachMagnitude: withinReachMagnitude * eurScale,
        targetRung: h ? h.targetRung : null,
        targetRungValue: h ? h.target : null,

        // Full per-lever ladder for the deep-dive simulator (rungs, active,
        // horizon, per-lever €) and the hero climb.
        ladder,

        eHeadroom: eHead,
        cHeadroom: cHead,

        zoneEmax: ceilings.Emax,
        zoneCmax: ceilings.Cmax,

        // Traffic share (descriptive) + the legacy sensitivity numbers, kept
        // for the deep-dive's traffic sliders. Reach now ALSO has a real
        // within-reach € via the ladder (reachPart / the reach leg).
        trafficShare: storeTotalVisitors > 0 ? z.visitors / storeTotalVisitors : null,
        trafficSensitivity: (() => {
          // Normalised to €/mo like the rest (currentRevenue is a period total).
          const currentRevenue = z.visitors * z.engagementRate * z.conversionRate * (z.avgValue || 0) * eurScale
          if (!(currentRevenue > 0)) return null
          return {
            pct10: Math.round(currentRevenue * 0.10),
            pct25: Math.round(currentRevenue * 0.25),
            pct50: Math.round(currentRevenue * 0.50),
          }
        })(),

        trendPct,
        trendClass
      }
    })
    .sort((a, b) => {
      if (a.muted !== b.muted) return a.muted ? 1 : -1
      // Sort by within-reach magnitude (not potentialImpact) so a counts-only
      // tripwire zone whose reach opportunity is in VISITORS still ranks — for a
      // measured zone magnitude === potentialImpact so ordering is unchanged.
      if (b.withinReachMagnitude !== a.withinReachMagnitude) return b.withinReachMagnitude - a.withinReachMagnitude
      return a.zone.localeCompare(b.zone)
    })
}

// The single canonical "store within reach €/mo" — the sum of every product
// zone's within-reach headline (computePriorities → potentialImpact). This is
// THE store opportunity number: the Today hero, the commercial-efficiency gauge,
// and the Actions list all read it so the headline always equals the sum of the
// per-zone cards below it (one number, everywhere). It rolls up the SAME per-zone
// ladder (reach + engagement + conversion, per-zone best lever) — never the
// engagement-only store-efficiency ladder, which double-counts differently.
export function sumWithinReach(priorities) {
  return (priorities || []).reduce((sum, p) => sum + (Number(p?.potentialImpact) || 0), 0)
}
